import { fileUriToPath, pathJoin } from '../lib';
import { FileCache, FileCacheOptions } from './FileCache';
import { FileCacheBackend } from './FileCacheBackend';
import {
  DEFAULT_FILE_CACHE_FILE_OPTIONS,
  FileCacheEvictResult,
  FileCacheFileInfo,
  FileCacheFileOptions,
  FileCacheInfo,
  FileCacheProgressCallback,
} from './FileCacheInterface';
import { FileSystemInterface } from './FileSystemInterface';

interface CacheNode {
  fileName: string;
  fileSize: number;
  lastUsedAt: number;
  addedAt: number;
  prev: CacheNode | null;
  next: CacheNode | null;
}

/** Accept either a file:// URI or a bare absolute path. */
const toAbsolutePath = (filePathOrUri: string): string => filePathOrUri.startsWith('file:') ? fileUriToPath(filePathOrUri) : filePathOrUri;

const baseName = (path: string): string => {
  const idx = path.lastIndexOf('/');
  return idx >= 0 ? path.slice(idx + 1) : path;
};

/** Download a URL to its bytes. Injectable so tests (and future auth) can override it. */
export type FetchBlob = (url: string) => Promise<Blob>;

/** Default fetcher: a plain GET via the global `fetch`. The thrown error carries `status` so callers can treat 404 as an expected miss. */
const defaultFetchBlob: FetchBlob = async url => {
  const res = await fetch(url);
  if (!res.ok) {
    throw Object.assign(
      new Error(`InMemoryFileCacheBackend: fetch failed for ${url}: ${res.status} ${res.statusText}`),
      { status: res.status },
    );
  }
  return res.blob();
};

export interface InMemoryFileCacheBackendOptions {
  fileSystem: FileSystemInterface;
  /** Directory under which cached files live. Trailing slash optional. */
  cacheDirectory: string;
  /** Override how URLs are downloaded (defaults to the global `fetch`). */
  fetchBlob?: FetchBlob;
  /**
   * Override the reported free-space value returned by {@link getFreeSpace}.
   * Defaults to 10 GB (effectively unbounded for in-memory use).
   * Useful in tests that want to exercise the free-space cap.
   */
  freeSpace?: number;
}

/**
 * Pure-persistence backend: an in-memory Map of metadata + a doubly-linked
 * LRU list, with bytes stored via the supplied {@link FileSystemInterface}.
 *
 * Knows nothing about a budget — no `maxCacheSize`, no auto-eviction. The
 * wrapper ({@link FileCache}) owns budget policy and drives eviction through
 * {@link evict}. No perf-caching layer either (no running counter, no sync
 * read surface, no hot mirror) — those also live in the wrapper.
 *
 * Complexity:
 *   addFile / getFile / getFileInfo / evictFile         O(1) on metadata
 *   evict                                               O(k) in files evicted
 *   freshen                                             O(|input|)
 *   getFiles                                            O(skipped + returned),
 *                                                       since the linked list is
 *                                                       always in lastUsedAt order
 *   getCacheSize / getFilesCount                        O(1)  (in-memory)
 *
 * Lock-free: every mutation of the list/map is synchronous, so it's atomic with
 * respect to any concurrent op — the only `await`s are filesystem/network I/O
 * that never span a read-then-write of the shared structure. There is no
 * per-backend mutex; what little serialization the cache needs (read-decide-act
 * on the byte budget) lives in {@link FileCache}.
 */
export class InMemoryFileCacheBackend implements FileCacheBackend {
  private readonly fileSystem: FileSystemInterface;
  private readonly cacheDirectory: string;
  private readonly fetchBlob: FetchBlob;
  private readonly reportedFreeSpace: number;

  // Doubly linked list, head = MRU (largest lastUsedAt), tail = LRU.
  private head: CacheNode | null = null;
  private tail: CacheNode | null = null;
  private readonly nodes: Map<string, CacheNode> = new Map();

  constructor({ fileSystem, cacheDirectory, fetchBlob, freeSpace }: InMemoryFileCacheBackendOptions) {
    this.fileSystem = fileSystem;
    // pathJoin handles trailing slashes — no need to normalize here.
    this.cacheDirectory = cacheDirectory;
    this.fetchBlob = fetchBlob ?? defaultFetchBlob;
    this.reportedFreeSpace = freeSpace ?? 10 * 1024 * 1024 * 1024;
  }

  private joinPath(fileName: string): string {
    return pathJoin(this.cacheDirectory, fileName);
  }

  private toInfo(node: CacheNode): FileCacheFileInfo {
    return {
      fileName: node.fileName,
      fileSize: node.fileSize,
      lastUsedAt: node.lastUsedAt,
      addedAt: node.addedAt,
      path: this.joinPath(node.fileName),
    };
  }

  private unlink(node: CacheNode): void {
    if (node.prev) node.prev.next = node.next;
    else this.head = node.next;
    if (node.next) node.next.prev = node.prev;
    else this.tail = node.prev;
    node.prev = null;
    node.next = null;
  }

  private insertAsHead(node: CacheNode): void {
    node.prev = null;
    node.next = this.head;
    if (this.head) this.head.prev = node;
    this.head = node;
    if (!this.tail) this.tail = node;
  }

  private touch(node: CacheNode): void {
    node.lastUsedAt = Date.now();
    if (this.head === node) return;
    this.unlink(node);
    this.insertAsHead(node);
  }

  /** Create + insert a fresh MRU node. Synchronous, so atomic against concurrent ops. */
  private insertNode(fileName: string, fileSize: number): FileCacheFileInfo {
    const now = Date.now();
    const node: CacheNode = { fileName, fileSize, lastUsedAt: now, addedAt: now, prev: null, next: null };
    this.nodes.set(fileName, node);
    this.insertAsHead(node);
    return this.toInfo(node);
  }

  private async ensureCacheDirectory(): Promise<void> {
    if (await this.fileSystem.getDirectoryExists(this.cacheDirectory)) return;
    await this.fileSystem.makeDirectoryAsync(this.cacheDirectory, { intermediates: true });
  }

  async initialize(): Promise<void> {
    await this.ensureCacheDirectory();
    const { files } = await this.fileSystem.getDirectoryContents(this.cacheDirectory);
    const now = Date.now();
    const entries: Array<{ fileName: string; size: number; mtime: number }> = [];
    for (const fileName of files) {
      if (this.nodes.has(fileName)) continue;
      const info = await this.fileSystem.getFileInfo(this.joinPath(fileName));
      if (!info.exists || info.size === undefined) continue;
      entries.push({ fileName, size: info.size, mtime: info.modificationTime ?? now });
    }
    // Ascending so repeated insertAsHead leaves the newest at head.
    entries.sort((a, b) => a.mtime - b.mtime);
    for (const e of entries) {
      const node: CacheNode = {
        fileName: e.fileName,
        fileSize: e.size,
        lastUsedAt: e.mtime,
        addedAt: e.mtime,
        prev: null,
        next: null,
      };
      this.nodes.set(e.fileName, node);
      this.insertAsHead(node);
    }
  }

  getFreeSpace(): number {
    return this.reportedFreeSpace;
  }

  async getCacheInfo(): Promise<FileCacheInfo> {
    return {
      cacheSize: this.computeTotal(),
      fileCount: this.nodes.size,
    };
  }

  async evict(bytes: number, onProgress?: FileCacheProgressCallback): Promise<FileCacheEvictResult> {
    const evicted: FileCacheFileInfo[] = [];
    let evictedBytes = 0;
    if (bytes <= 0) {
      onProgress?.(1);
      return { evicted, evictedBytes };
    }
    while (evictedBytes < bytes && this.tail) {
      const node = this.tail;
      evicted.push(this.toInfo(node));
      this.unlink(node);
      this.nodes.delete(node.fileName);
      evictedBytes += node.fileSize;
      try {
        await this.fileSystem.deleteAsync(this.joinPath(node.fileName));
      } catch {
        // Best-effort: metadata is the source of truth.
      }
      // Clamp to [0, 1] — progress is bytes-freed / bytes-requested and must never exceed 1.
      onProgress?.(Math.max(0, Math.min(evictedBytes / bytes, 1)));
    }
    // The cache may have emptied before reaching `bytes`; ensure a final 1.
    if (evictedBytes < bytes) onProgress?.(1);
    return { evicted, evictedBytes };
  }

  async getFiles(options?: FileCacheFileOptions): Promise<FileCacheFileInfo[]> {
    const merged = { ...DEFAULT_FILE_CACHE_FILE_OPTIONS, ...options };
    const limit = merged.limit ?? Infinity;
    const descending = merged.descending !== false;
    const cursor = merged.lastUsedAt;

    const result: FileCacheFileInfo[] = [];
    let node: CacheNode | null = descending ? this.head : this.tail;
    while (node && result.length < limit) {
      const include = cursor === undefined ? true : descending ? node.lastUsedAt < cursor : node.lastUsedAt > cursor;
      if (include) result.push(this.toInfo(node));
      node = descending ? node.next : node.prev;
    }
    return result;
  }

  async getFileToAddInfo(srcPath: string): Promise<{ fileName: string; fileSize: number }> {
    const sourcePath = toAbsolutePath(srcPath);
    const fsInfo = await this.fileSystem.getFileInfo(sourcePath);
    if (!fsInfo.exists || fsInfo.size === undefined) {
      throw new Error(`InMemoryFileCacheBackend.getFileToAddInfo: source file not found: ${sourcePath}`);
    }
    return { fileName: baseName(sourcePath), fileSize: fsInfo.size };
  }

  async addFile(srcPath: string): Promise<FileCacheFileInfo> {
    const sourcePath = toAbsolutePath(srcPath);
    const fileName = baseName(sourcePath);
    const destPath = this.joinPath(fileName);

    const fsInfo = await this.fileSystem.getFileInfo(sourcePath);
    if (!fsInfo.exists || fsInfo.size === undefined) {
      throw new Error(`InMemoryFileCacheBackend.addFile: source file not found: ${sourcePath}`);
    }

    // The wrapper guarantees there's room and no same-name entry — a clean insert.
    await this.ensureCacheDirectory();
    if (sourcePath !== destPath) {
      await this.fileSystem.moveAsync({ from: sourcePath, to: destPath });
    }

    // The list/map mutation below is synchronous, so it's atomic with respect to
    // any concurrent op — no per-backend mutex needed.
    return this.insertNode(fileName, fsInfo.size);
  }

  async addFileFromUrl(fileName: string, url: string): Promise<FileCacheFileInfo> {
    if (!this.fileSystem.writeAsBlobAsync) {
      throw new Error('InMemoryFileCacheBackend.addFileFromUrl: fileSystem.writeAsBlobAsync is not available');
    }
    // Download + write are pure I/O (no shared-state access), so they run lock-free
    // and concurrent downloads proceed in parallel.
    const blob = await this.fetchBlob(url);
    await this.ensureCacheDirectory();
    await this.fileSystem.writeAsBlobAsync(this.joinPath(fileName), blob);
    return this.insertNode(fileName, blob.size);
  }

  async getFile(fileName: string): Promise<string | undefined> {
    const node = this.nodes.get(fileName);
    if (!node) return undefined;
    this.touch(node);
    return this.joinPath(fileName);
  }

  async getFileInfo(fileName: string): Promise<FileCacheFileInfo | undefined> {
    const node = this.nodes.get(fileName);
    return node ? this.toInfo(node) : undefined;
  }

  async getFileInfos(fileNames: string[]): Promise<Map<string, FileCacheFileInfo>> {
    const result = new Map<string, FileCacheFileInfo>();
    for (const fileName of fileNames) {
      const node = this.nodes.get(fileName);
      if (node) result.set(fileName, this.toInfo(node));
    }
    return result;
  }

  async evictFile(fileName: string): Promise<FileCacheEvictResult> {
    const node = this.nodes.get(fileName);
    if (!node) return { evicted: [], evictedBytes: 0 };
    // Remove from the list/map synchronously (atomic), then delete the bytes.
    const info = this.toInfo(node);
    this.unlink(node);
    this.nodes.delete(fileName);
    try {
      await this.fileSystem.deleteAsync(this.joinPath(fileName));
    } catch {
      // Best-effort.
    }
    return { evicted: [info], evictedBytes: info.fileSize };
  }

  async freshen(fileNames: string[]): Promise<{ missingKeys: string[] }> {
    const seen = new Set<string>();
    const missingKeys: string[] = [];
    const now = Date.now();
    for (const fileName of fileNames) {
      if (seen.has(fileName)) continue;
      seen.add(fileName);
      const node = this.nodes.get(fileName);
      if (!node) {
        missingKeys.push(fileName);
        continue;
      }
      node.lastUsedAt = now;
      if (this.head !== node) {
        this.unlink(node);
        this.insertAsHead(node);
      }
    }
    return { missingKeys };
  }

  /**
   * Compute the running total over the in-memory map. O(n) but only called at
   * eviction boundaries and from {@link getCacheSize} (which the wrapper hits
   * once at init to prime its counter). The wrapper's cached counter serves
   * every subsequent read.
   */
  private computeTotal(): number {
    let total = 0;
    for (const node of this.nodes.values()) total += node.fileSize;
    return total;
  }
}

/**
 * Construction options: the backend's filesystem + directory, plus the wrapper's
 * budget (`maxCacheSize`) and optional `mirrorMax`.
 */
export type InMemoryFileCacheOptions = InMemoryFileCacheBackendOptions & FileCacheOptions;

/**
 * Convenience: a FileCache wrapping an InMemoryFileCacheBackend. Backward-compatible
 * with the previous `new InMemoryFileCache(...)` shape.
 */
export class InMemoryFileCache extends FileCache {
  constructor(
    { fileSystem, cacheDirectory, fetchBlob, freeSpace, maxCacheSize, freePaddingBytes, minCacheSize, fileInfoCacheMaxEntries }: InMemoryFileCacheOptions,
  ) {
    super(
      new InMemoryFileCacheBackend({ fileSystem, cacheDirectory, fetchBlob, freeSpace }),
      { maxCacheSize, freePaddingBytes, minCacheSize, fileInfoCacheMaxEntries },
    );
  }
}
